import { computed, defineComponent, getCurrentInstance, useContext } from '@nuxtjs/composition-api';
import { useTask } from 'vue-concurrency';
import { INJECTABLE_LISTS, PRESETS, SOCIAL_LINKS_TYPES } from './data';
import { ADFOX_CODES_DESKTOP, ADFOX_CODES_MOBILE } from '~/components/Adfox';
import { createGridChunks, getArticlesSize } from '~/composables';
import { columns } from '~/composables/grid/rubric';
import { useDuck } from '~/services';
import { useAsyncRef, createHead, isFlutter } from '~/utils';
import { EDUCATION_TAGS_REGEXP, LIST_EMPTY_TITLE_KEYWORDS } from '~/constants';
/**
 * Duck service.
 */
const duck = useDuck();
export default defineComponent({
    name: 'PageRubric',
    setup() {
        const context = useContext();
        const viewport = context.$viewport;
        /**
         * Instance.
         */
        const instance = getCurrentInstance();
        const isDone = useAsyncRef(instance, 'isDone');
        /**
         * Adfox configs
         */
        const adfoxConfigs = {
            default: { ...ADFOX_CODES_DESKTOP['300x600_1'], shouldSyncSession: true },
            background: ADFOX_CODES_DESKTOP.background,
        };
        /**
         * Articles.
         */
        const articles = useAsyncRef(instance, 'articles');
        const presets = useAsyncRef(instance, 'presets');
        const shouldUseTaxList = useAsyncRef(instance, 'shouldUseTaxList');
        const listsVisibility = useAsyncRef(instance, 'listsVisibility');
        const shouldRenderAds = computed(() => !isFlutter(context.$device.userAgent));
        /**
         * Current preset.
         */
        const articlesCards = computed(() => getArticlesSize(viewport.breakpoint, {
            columns,
            presets: presets.value,
        }));
        /**
         * Grids.
         */
        const grids = computed(() => createGridChunks(articles.value, articlesCards.value));
        const pageOffset = useAsyncRef(instance, 'pageOffset');
        /**
         * Fetch articles task.
         */
        const fetchArticlesTask = useTask(function* (signal) {
            if (fetchArticlesTask.isRunning || isDone.value) {
                return;
            }
            signal.addEventListener('abort', () => duck.abortRequest('getPubTax'));
            const url = (context?.route.value?.path || '').replace(/^\/|\/$/g, '');
            try {
                if (!url) {
                    return;
                }
                let newArticles = [];
                if (shouldUseTaxList.value) {
                    const { data: list } = yield duck.pub.getPubListById(url, {
                        'list[limit]': String(articlesCards.value),
                        'list[offset]': String(articles.value.length * (pageOffset.value + 1)),
                    });
                    newArticles = list.articles.map((article) => article.data);
                }
                else {
                    const { data: list } = yield duck.pub.getPubTax({
                        'list[limit]': articlesCards.value,
                        'list[offset]': articles.value.length * (pageOffset.value + 1),
                        'tax[url]': url,
                    });
                    newArticles = list.articles;
                }
                if (newArticles.length < articlesCards.value) {
                    isDone.value = true;
                }
                if (!newArticles.length) {
                    return;
                }
                articles.value = [...articles.value, ...newArticles];
            }
            catch (error) {
                console.error(error);
            }
        });
        /**
         * Bind CardAdfox attrs
         * @param gridKey
         */
        function bindCardAdfox(gridKey) {
            if (viewport.isLessThan('tablet')) {
                const BANNERS_COUNT = 6;
                let options = ADFOX_CODES_MOBILE['300x250_6'];
                if (gridKey < BANNERS_COUNT) {
                    options = ADFOX_CODES_MOBILE[`300x250_${gridKey + 1}`];
                }
                return {
                    options,
                    size: {
                        height: 0,
                        width: 1,
                    },
                    type: 'ad-mobile',
                };
            }
            return {
                options: {
                    ...ADFOX_CODES_DESKTOP.billboard_2,
                },
                size: {
                    height: 0,
                    width: 3,
                },
                type: 'ad',
            };
        }
        function handleGridIntersect(gridKey) {
            gridKey = gridKey + 1 + pageOffset.value;
            history.replaceState(null, '', gridKey > 1 ? `?page=${gridKey}` : context.route.value.fullPath);
        }
        function getListsVisibility(gridKey) {
            return gridKey === 0
                ? listsVisibility.value
                : Object.keys(listsVisibility.value).reduce((items, key) => {
                    items[key] = false;
                    return items;
                }, {});
        }
        return {
            SOCIAL_LINKS_TYPES,
            articlesCards,
            bindCardAdfox,
            columns,
            adfoxConfigs,
            fetchArticlesTask,
            getListsVisibility,
            grids,
            handleGridIntersect,
            isDone,
            listsVisibility,
            shouldRenderAds,
        };
    },
    async asyncData(context) {
        const { app, env, error, route } = context;
        try {
            const path = route.path.replace(/^\/|\/$/g, '');
            const isRubricCompanyNews = path === 'novosti-kompaniy';
            const isRubricEducation = path.startsWith('education');
            const isTagEducation = path.startsWith('tegi/education');
            const isRubric = route.name === 'rubric';
            const isTag = route.name === 'tegi-id';
            let isDone = false;
            let pageKey = '';
            const pageKeyFallback = isRubric ? 'rubric' : 'tag';
            switch (true) {
                case isRubricCompanyNews:
                    pageKey = 'rubricCompanyNews';
                    break;
                case isRubricEducation:
                    pageKey = 'rubricEducation';
                    break;
                case isTagEducation:
                    pageKey = 'tagEducation';
                    break;
                case isTag:
                    pageKey = 'tag';
                    break;
                case isRubric:
                default:
                    pageKey = 'rubric';
                    break;
            }
            const presets = PRESETS[pageKey] || PRESETS[pageKeyFallback];
            const limit = getArticlesSize(app.$viewport.breakpoint, {
                columns,
                presets,
            });
            const pageNumber = Number(route.query.page) || 0;
            const pageOffset = pageNumber > 0 ? pageNumber - 1 : 0;
            let articles = [];
            let title = '';
            let shouldUseTaxList = true;
            // Try getting the list.
            try {
                const { data: list } = await duck.pub.getPubListById(path, {
                    'list[limit]': String(limit),
                    'list[offset]': String(limit * pageOffset),
                });
                if (!list.articles?.length) {
                    throw new Error('Not found');
                }
                if (list.articles.length < limit) {
                    isDone = true;
                }
                articles = list.articles.map((article) => article.data);
                title = list.blockName || list.name;
                title = !LIST_EMPTY_TITLE_KEYWORDS.includes(title) ? title : '';
            }
            catch {
                shouldUseTaxList = false;
            }
            if (!shouldUseTaxList) {
                // Try getting the tax.
                const { data: list } = await duck.pub.getPubTax({
                    'list[limit]': limit,
                    'list[offset]': limit * pageOffset,
                    'tax[url]': path,
                });
                if (!list.articles?.length) {
                    throw new Error('Not found');
                }
                if (list.articles.length < limit) {
                    isDone = true;
                }
                articles = list.articles;
                title = list.name || '';
            }
            // Handle injectable lists
            const injectableLists = INJECTABLE_LISTS(path);
            const lists = injectableLists[pageKey] || injectableLists[pageKeyFallback] || {};
            const listsData = {};
            const listsVisibility = {};
            if (Object.keys(lists).length) {
                const tasks = await Promise.allSettled(Object.keys(lists).map(async (id) => {
                    const { data } = await duck.pub.getPubListById(id);
                    return data;
                }));
                for (const task of tasks) {
                    let cardsKeys;
                    if (task.status === 'fulfilled') {
                        cardsKeys = lists[task?.value?.slug || ''];
                        if (!cardsKeys) {
                            continue;
                        }
                        /**
                         * Нужен refactor
                         * !isRubricEducation временно решение
                         */
                        for (const cardKey of Object.keys(cardsKeys)) {
                            listsData[cardKey] = task.value;
                            listsVisibility[cardKey] = !isRubricEducation;
                        }
                    }
                    else {
                        const splittedUrl = task?.reason?.url?.split('/') || [];
                        if (!splittedUrl.length) {
                            continue;
                        }
                        const slug = splittedUrl[splittedUrl.length - 1];
                        cardsKeys = lists[slug];
                        if (!cardsKeys) {
                            continue;
                        }
                        for (const cardKey of Object.keys(cardsKeys)) {
                            listsVisibility[cardKey] = false;
                        }
                    }
                }
            }
            const head = {
                canonical: env.HOST + route.path,
                title,
                url: env.HOST + route.path,
            };
            if (isTag) {
                title = title.trim().replace(EDUCATION_TAGS_REGEXP, '');
                title = title.charAt(0).toUpperCase() + title.slice(1);
                head.description = `Главное по теме «${title}» – читайте на сайте Forbes.ru.`;
                head.title = `${title} – новости и статьи по тегу`;
            }
            context.$page = app.$page = {
                title,
            };
            return {
                SOCIAL_LINKS_TYPES,
                $page: context.$page,
                articles,
                head,
                isDone,
                lists,
                listsData,
                listsVisibility,
                presets,
                pageOffset,
                shouldUseTaxList,
                title,
            };
        }
        catch (e) {
            console.error(e);
            error({ statusCode: 404 });
        }
    },
    head() {
        return createHead(this.head);
    },
});
